import React, { useRef, useLayoutEffect, useState } from "react";

const rosDiagramCropped = "./images/arm.png";
const hotlineImage = "./images/hotline.png";
const dynamicsImage = "./images/dynamics.png";
const rlDiagram = "./images/RLdiagram.png";
const xlaImage = "./images/xla.png";
const lifespanImage = "./images/lifespan.png";
const algobenchImage = "./images/algos_fig.png";

import { ContactInfo } from "./App";


function Cite(props) {
  return (
    <div className="pub-bibtex">
      <pre className={props.abstract ? "pub-abstact-text" : "pub-cite-text"}>{props.cite}</pre>
      <button
        className="pub-bibtex-copy"
        onClick={() => {navigator.clipboard.writeText(props.cite)}}
      >
        Copy
      </button>
    </div>
  )
}

function Publication(props) {
  var showCitationButton;
  const [showCitation, setShowCitation] = useState(false);
  function toggleShowCitation() {
    setShowCitation(!showCitation);
  }

  if (showCitation) {
    showCitationButton = (
      <a onClick={toggleShowCitation}>
        Hide BibTeX
      </a>
    );
  } else {
    showCitationButton = (
      <a onClick={toggleShowCitation}>
        BibTeX
      </a>
    );
  }

  var showAbstractButton;
  const [showAbstract, setShowAbstract] = useState(false);
  function toggleShowAbstract() {
    setShowAbstract(!showAbstract);
  }

  if (showAbstract) {
    showAbstractButton = (
      <a onClick={toggleShowAbstract}>
        Hide Abstract
      </a>
    );
  } else {
    showAbstractButton = (
      <a onClick={toggleShowAbstract}>
        Abstract
      </a>
    );
  }

  return (
    <div>
      <div className="pub-container">
        <div className="pub-image-container">
          <img className="pub-image" src={props.image} style={{...props.extraStyle}}/>
        </div>
        <div className="pub-info">
          <div className="pub-title">{props.title}</div>
          <div className="pub-authors">{props.authors}</div>
          <div className="pub-conference">{props.conference}</div>
          <div className="pub-links">
            {props.children}
            {props.paperUrl && <a target="_blank" href={props.paperUrl}>Paper</a> }
            {props.abstract && <span>&nbsp;|&nbsp;{showAbstractButton}</span> }
            {props.cite && <span>&nbsp;|&nbsp;{showCitationButton}</span> }
            {props.websiteUrl && <span>&nbsp;|&nbsp;<a target="_blank" href={props.websiteUrl}>Website</a></span> }
          </div>
        </div>
      </div>
      {showAbstract ? <Cite cite={props.abstract} abstract/> : null}
      {showCitation ? <Cite cite={props.cite}/> : null}
    </div>
  );
}


export function FeaturedPublications(props){
  return (
    <div>
      <Publication
        title={
          "Hotline Profiler: Automatic Annotation and A Multi-Scale Timeline for Visualizing Time-Use in DNN Training"
        }
        authors={
          <span>
            <u>Daniel Snider</u>, Fanny Chevalier, Gennady Pekhimenko
          </span>
        }
        conference={
          "Paper in Proceedings of Machine Learning and Systems (MLSys 2023)"
        }
        image={hotlineImage}
        websiteUrl={"./hotline"}
        paperUrl={"./papers/Hotline.pdf"}
        cite={`
@article{2023Hotline,
  title={Hotline Profiler: Automatic Annotation and A Multi-Scale Timeline for Visualizing Time-Use in DNN Training},
  author={Snider, Daniel and Chevalier, Fanny and Pekhimenko, Gennady},
  journal={Conference on Machine Learning and Systems (MLSys)},
  year={2023}
}`}
        abstract={`Profiling is a standard practice used to investigate the efficiency of software and hardware operation at runtime and is a crucial part of proving new concepts, debugging problems, and optimizing performance. However, most machine learning (ML) developers find profiling secondary to their goal of improving model accuracy or just too difficult (especially with existing ML tools). As a result, profiling is frequently an afterthought, and so many ML developers rely on opaque metrics such as iteration time and GPU utilization which give little insight into why ML training may be slow. This leads developers to spend excessive time investigating performance issues or to write code without understanding the problem. In this work, we aim to reach the large group of ML developers who currently do not profile their deep neural network (DNN) training workloads or are not happy with existing tools.

To help ML developers investigate and understand time-use in DNN training, we propose HOTLINE, a novel profiler designed specifically for runtime bottleneck identification. HOTLINE is the first profiler to automatically annotate a standard data format for program runtime traces with DNN concepts that most ML developers are familiar with, i.e. the DNN training loop and model architecture. HOTLINE does so without modifying DNN libraries or making use of vendor-specific tools and introduces no additional overhead on measurements. We further introduce noise reduction techniques and a multi-scale timeline visualization to make the presentation of DNN runtime data more insightful, familiar, and easy to navigate. We demonstrate HOTLINE’s utility through in-depth case studies of finding bottlenecks in real-world DNN applications and we report on a user study with 17 software developers in which most participants were able to perform common performance investigation tasks in under 30 seconds (avg = 26 sec) and further commented that HOTLINE’s visualization “Takes less time to find insights compared to existing approaches”.`}
      />

      <Publication
        title={"Benchmarking Neural Network Training Algorithms"}
        authors={
          <span>
            George Dahl, Frank Schneider, Zachary Nado, <u>Daniel Snider</u>,
            others, Peter Mattson
          </span>
        }
        conference={
          "Preprint to be submitted to Journal of Machine Learning Research, 2023"
        }
        image={algobenchImage}
        paperUrl={"https://arxiv.org/abs/2306.07179"}
        websiteUrl={"https://mlcommons.org/benchmarks/algorithms/"}
        cite={`@article{2023benchmarking,
  title={Benchmarking Neural Network Training Algorithms},
  author={George Dahl, Frank Schneider, Zachary Nado, Daniel Snider, and others, Peter Mattson},
  journal={arxiv},  
  year={2023},
}`}
        abstract={`Training algorithms, broadly construed, are an essential part of every deep learning pipeline. Training algorithm improvements that speed up training across a wide variety of workloads (e.g., better update rules, tuning protocols, learning rate schedules, or data selection schemes) could save time, save computational resources, and lead to better, more accurate, models. Unfortunately, as a community, we are currently unable to reliably identify training algorithm improvements, or even determine the state-of-the-art training algorithm. In this work, using concrete experiments, we argue that real progress in speeding up training requires new benchmarks that resolve three basic challenges faced by empirical comparisons of training algorithms: (1) how to decide when training is complete and precisely measure training time, (2) how to handle the sensitivity of measurements to exact workload details, and (3) how to fairly compare algorithms that require hyperparameter tuning. In order to address these challenges, we introduce a new, competitive, time-to-result benchmark using multiple workloads running on fixed hardware, the AlgoPerf: Training Algorithms benchmark. Our benchmark includes a set of workload variants that make it possible to detect benchmark submissions that are more robust to workload changes than current widely-used methods. Finally, we evaluate baseline submissions constructed using various optimizers that represent current practice, as well as other optimizers that have recently received attention in the literature. These baseline results collectively demonstrate the feasibility of our benchmark, show that non-trivial gaps between methods exist, and set a provisional state-of-the-art for future benchmark submissions to try and surpass.`}
      />

      <Publication
        title={"Optimizing Data Collection in Deep Reinforcement Learning"}
        authors={
          <span>
            James Gleeson, <u>Daniel Snider</u>, Yvonne Yang, Eyal de Lara, and
            Gennady Pekhimenko
          </span>
        }
        conference={
          "Paper in Workshop on Benchmarking ML Workloads (MLBench) at Conference on Machine Learning and Systems (MLSys 2022)"
        }
        image={rlDiagram}
        extraStyle={{ opacity: "65%" }}
        paperUrl={
          "./papers/Optimizing_Data_Collection_in_Deep_Reinforcement_Learning.pdf"
        }
        cite={`@article{2022OptimizingDC,
  title={Optimizing Data Collection in Deep Reinforcement Learning},
  author={Gleeson, James and Snider, Daniel and Yang, Yvonne and Gabel, Moshe and de Lara, Eyal and Pekhimenko, Gennady},
  journal={Workshop on Benchmarking ML Workloads (MLBench) at Conference on Machine Learning and Systems (MLSys)},
  url={https://arxiv.org/abs/2207.07736},
  year={2022}
}`}
        abstract={`Reinforcement learning (RL) workloads take a notoriously long time to train due to the large number of samples collected at runtime from simulators. Unfortunately, cluster scale-up approaches remain expensive, and commonly used CPU implementations of simulators induce high overhead when switching back and forth between GPU computations. We explore two optimizations that increase RL data collection efficiency by increasing GPU utilization: (1) GPU vectorization: parallelizing simulation on the GPU for increased hardware parallelism, and (2) simulator kernel fusion: fusing multiple simulation steps to run in a single GPU kernel launch to reduce global memory bandwidth requirements. We find that GPU vectorization can achieve up to 1024× speedup over commonly used CPU simulators. We profile the performance of different implementations and show that for a simple simulator, ML compiler implementations (XLA) of GPU vectorization outperform a DNN framework (PyTorch) by 13.4× by reducing CPU overhead from repeated Python to DL backend API calls. We show that simulator kernel fusion speedups with a simple simulator are 11.3× and increase by up to 1024× as simulator complexity increases in terms of memory bandwidth requirements. We show that the speedups from simulator kernel fusion are orthogonal and combinable with GPU vectorization, leading to a multiplicative speedup.`}
      />
    </div>
  );
}

export function AdditionalPublications(props){
  return (
    <div>
      <Publication
        title={
          "A Study of the Effects of Model Architecture Ablations on Training Dynamics"
        }
        authors={
          <span>
            Adamo Young, <u>Daniel Snider</u>, Jinyue Yeng
          </span>
        }
        conference={"Course Paper, University of Toronto (U of T), 2022."}
        image={dynamicsImage}
        paperUrl={"./papers/Training_Dynamics_Model_Architecture.pdf"}
        cite={`@article{2022Ablations,
  title={A Study of the Effects of Model Architecture Ablations on Training Dynamics},
  author={Young, Adamo and Snider, Daniel and Yeng, Jinyue},
  journal={Course Project, University of Toronto},
  url={https://danielsnider.ca/papers/Training_Dynamics_Model_Architecture.pdf},
  year={2022}
}`}
        abstract={`In this study we investigate how neural network architecture ablations affect training dynamics. We implement an array of eigenspectrum-based evaluation metrics and look into the effects of activation functions, model depth and width, as well as batch norms on optimization. We conduct our analysis on multi-layer perceptron (MLP) models trained on the MNIST dataset, as well as graph neural networks (GNN) trained on the MolPCBA dataset. Our eigenspectrum metrics seems to capture certain model behaviours, but are not entirely consistent with previous work. We further illustrate advantages and limitations of these metrics on a toy low rank matrix approximation problem. Our work contributes to a public research project called the "Algorithmic Efficiency Benchmark". `}
      />

      <Publication
        title={"Operator Fusion in XLA: Analysis and Evaluation"}
        authors={
          <span>
            <u>Daniel Snider</u>, Ruofan Liang
          </span>
        }
        conference={"Course Paper, University of Toronto (U of T), 2021."}
        image={xlaImage}
        paperUrl={"https://arxiv.org/pdf/2301.13062.pdf"}
        cite={`@article{2021xla,
  title={Operator Fusion in XLA: Analysis and Evaluation},
  author={Snider, Daniel and Liang, Ruofan},
  journal={Course Project, University of Toronto},
  url={https://arxiv.org/abs/2301.13062},
  year={2021}
}`}
        abstract={`Machine learning (ML) compilers are an active area of research because they offer the potential to automatically speedup tensor programs. Kernel fusion is often cited as an important optimization performed by ML compilers. However, there exists a knowledge gap about how XLA, the most common ML compiler, applies this nuanced optimization, what kind of speedup it can afford, and what low-level effects it has on hardware. Our paper aims to bridge this knowledge gap by studying key compiler passes of XLA’s source code. Our evaluation on a reinforcement learning environment Cartpole shows how different fusion decisions in XLA are made in practice. Furthermore, we implement several XLA kernel fusion strategies that can achieve up to 10.56x speedup compared to our baseline implementation. `}
      />

      <Publication
        title={
          "Postnatal Exocrine Pancreas Growth by Cellular Hypertrophy Correlates with a Shorter Lifespan in Mammals"
        }
        authors={
          <span>
            Shira Anzi, Miri Stolovich-Rain, <u>Daniel Snider</u>, and others,
            Ran Kafri, Yuval Dor
          </span>
        }
        conference={"Developmental Cell, 2018."}
        image={lifespanImage}
        paperUrl={
          "https://www.sciencedirect.com/science/article/pii/S1534580718304179"
        }
        cite={`@article{anzi2018postnatal,
  title={Postnatal exocrine pancreas growth by cellular hypertrophy correlates with a shorter lifespan in mammals},
  author={Anzi, Shira and Stolovich-Rain, Miri and Klochendler, Agnes and Fridlich, Ori and Helman, Aharon and Paz-Sonnenfeld, Avital and Avni-Magen, Nili and Kaufman, Elizabeth and Ginzberg, Miriam B and Snider, Daniel and others},
  journal={Developmental cell},
  volume={45},
  number={6},
  pages={726--737},
  year={2018},
  publisher={Elsevier}
}`}
        abstract={`Developmental processes in different mammals are thought to share fundamental cellular mechanisms. We report a dramatic increase in cell size during postnatal pancreas development in rodents, accounting for much of the increase in organ size after birth. Hypertrophy of pancreatic acinar cells involves both higher ploidy and increased biosynthesis per genome copy; is maximal adjacent to islets, suggesting endocrine to exocrine communication; and is partly driven by weaning-related processes. In contrast to the situation in rodents, pancreas cell size in humans remains stable postnatally, indicating organ growth by pure hyperplasia. Pancreatic acinar cell volume varies 9-fold among 24 mammalian species analyzed, and shows a striking inverse correlation with organismal lifespan. We hypothesize that cellular hypertrophy is a strategy for rapid postnatal tissue growth, entailing life-long detrimental effects. `}
      />

      <Publication
        title={
          "University Rover Challenge: Team Survey on Robotic Software Architecture"
        }
        authors={
          <span>
            <u>Daniel Snider</u>, Matthew Mirvish, Michal Barcis, Vatan Aksoy
            Tezer
          </span>
        }
        conference={
          "Book chapter in Robot Operating System (ROS) The Complete Reference (Volume 3), Springer, 2018"
        }
        image={rosDiagramCropped}
        paperUrl={
          "./papers/University_Rover_Challenge_Team_Survey_on_Robotic_Software_Architecture.pdf"
        }
        cite={`@article{2018rover,
  title={University Rover Challenge: Tutorials and Team Survey},
  author={Snider, Daniel and Mirvish, Matthew and Barcis, Michal and Tezer, Vatan Aksoy},
  journal={Robot Operating System (ROS) The Complete Reference (Volume 3)},
  pages={315--370},
  year={2018},
  publisher={Springer}
}`}
        abstract={`In this tutorial chapter we present a guide to building a robot through 11 tutorials. We prescribe simple software solutions to build a wheeled robot and manipulator arm that can autonomously drive and be remotely controlled. These tutorials are what worked for several teams at the University Rover Challenge 2017 (URC). Certain tutorials provide a quick start guide to using existing Robot Operating System (ROS) tools. Others are new contributions, or explain challenging topics such as wireless communication and robot administration. We also present the results of an original survey of 8 competing teams to gather information about trends in URC’s community, which consists of hundreds of university students on over 80 teams. Additional topics include satellite mapping of robot location (mapviz), GPS integration (original code) to autonomous navigation (move base), and more. We hope to promote collaboration and code reuse.`}
      />
    </div>
  );
}

export function PublicationsPage (props) {
  return (
    <div>
      <div className="main header">
        <div className="grey-color-bar"></div>
        <div className="secondary-page">
          {/* <ContactInfo/> */}
          <div style={{padding: '20px 20px 90px 20px '}}>
            <a href="/">« Home</a>
            <h2 className="underlined">Publications</h2>
            <FeaturedPublications/>
            <AdditionalPublications/>
            <a target="_blank" href="https://scholar.google.ca/citations?user=YzZrjVkAAAAJ">
              See on Google Scholar »
            </a>
          </div>
        </div>
      </div>
      <div className="grey-color-bar"></div>
    </div>
  )
}
